'use client';

/**
 * 议程编辑页（v1.0）
 *
 * 路由：/meetingattendance/[meetingId]/agenda/edit
 * 权限：meeting:agenda:update（MeetingManager / Administrator / 会议 creator）
 *
 * 布局：左右两栏
 *  - 左：议程 tree（段 + 项嵌套，拖排序）+ 加段 / 加项 / 删段 / 删项 / 分配任务 / 上传资料
 *  - 右：选中议程项后编辑表单（title / description / code / timeMinutes / presenter / categoryTag）
 *
 * 显式 save（点保存按钮），避免实时 PATCH 噪音。
 * 会议结束 / 取消后改议程：进入页面弹 warning 二次确认。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/lib/auth';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import {
  agendaApi,
  type AgendaTree as AgendaTreeData,
  type MeetingAgendaItem,
  type MeetingAgendaSection,
} from '@/services/api/agenda';
import { attachmentApi } from '@/services/api/attachment';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { ApiClientError } from '@/lib/api-client';
import { UploadDialog } from '@/components/file-upload';
import { AgendaTree } from './AgendaTree';
import { ItemEditForm } from './ItemEditForm';
import { AssignTaskDialog } from './AssignTaskDialog';

interface MeetingLite {
  id: string;
  title: string;
  status: string;
  creator?: { id: string };
}

export default function AgendaEditPage() {
  useAuthGuard();
  const router = useRouter();
  const params = useParams();
  const meetingId = useMemo(() => {
    const raw = params?.meetingId;
    return Array.isArray(raw) ? raw[0] : raw || '';
  }, [params]);
  const { t } = useTranslation();
  const tag = t.meetingAttendance.agenda;
  const { user, hasAnyRole, isAdmin } = useAuth();

  const [meeting, setMeeting] = useState<MeetingLite | null>(null);
  const [tree, setTree] = useState<AgendaTreeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<MeetingAgendaItem | null>(null);

  const [showEndedWarning, setShowEndedWarning] = useState(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  const [assignFor, setAssignFor] = useState<MeetingAgendaItem | null>(null);
  const [uploadFor, setUploadFor] = useState<MeetingAgendaItem | null>(null);

  // ─── 数据加载 ──────────────────────────────────────────────────────────

  const reload = useCallback(async () => {
    if (!meetingId) return;
    try {
      const data = await agendaApi.getMeetingAgenda(meetingId);
      setTree(data);
      // 同步刷新选中 item 的内容（其他字段如 attachments / uploadTasks 可能已变）
      setSelectedItem((prev) => {
        if (!prev) return prev;
        for (const sec of data.sections) {
          const found = sec.items.find((it) => it.id === prev.id);
          if (found) {
            setSelectedSectionId(sec.id);
            return found;
          }
        }
        return null;
      });
    } catch (err) {
      const code = err instanceof ApiClientError ? err.code : undefined;
      if (code === 'AGENDA_FORBIDDEN_NOT_MANAGER_OR_CREATOR' || (err as ApiClientError)?.statusCode === 403) {
        setPermissionDenied(true);
      } else {
        toast.error(t.meetingAttendance.myTasks.loadFailed);
      }
    }
  }, [meetingId, t.meetingAttendance.myTasks.loadFailed]);

  useEffect(() => {
    let cancelled = false;
    if (!meetingId) return;
    (async () => {
      try {
        setLoading(true);
        const m = await meetingAttendanceApi.getMeeting(meetingId);
        if (cancelled) return;
        const meetingObj: MeetingLite = m as any;
        setMeeting(meetingObj);

        // 前端权限拦截：admin / 会议 manager 角色 / 创建人
        const canEdit =
          isAdmin || hasAnyRole(MEETING_MANAGEMENT_ROLES) || meetingObj.creator?.id === user?.id;
        if (!canEdit) {
          setPermissionDenied(true);
          return;
        }

        // 已结束 / 取消，弹 warning
        if (meetingObj.status === 'COMPLETED' || meetingObj.status === 'CANCELLED') {
          setShowEndedWarning(true);
        }

        await reload();
      } catch (err) {
        if (cancelled) return;
        const code = err instanceof ApiClientError ? err.code : undefined;
        if ((err as ApiClientError)?.statusCode === 403) {
          setPermissionDenied(true);
        } else if ((err as ApiClientError)?.statusCode === 404) {
          toast.error(code || 'NOT_FOUND');
          router.push('/meetingattendance/meetings');
        } else {
          toast.error(t.meetingAttendance.myTasks.loadFailed);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  // ─── 议程项上传文件（item 维度） ─────────────────────────────────────

  const itemUploader = useCallback(
    async (
      file: File,
      opts: { onProgress: (p: number) => void; signal: AbortSignal },
    ) => {
      if (!uploadFor) return;
      await attachmentApi.uploadAgendaItemAttachment(uploadFor.id, file, {
        signal: opts.signal,
        onProgress: (e) => opts.onProgress(e.percent),
      });
    },
    [uploadFor],
  );

  // ─── 选 item / 保存 ──────────────────────────────────────────────────

  const handleSelectItem = useCallback((sectionId: string, item: MeetingAgendaItem) => {
    setSelectedSectionId(sectionId);
    setSelectedItem(item);
  }, []);

  const handleItemSaved = useCallback(
    (next: MeetingAgendaItem) => {
      setSelectedItem(next);
      // 把局部更新合并回 tree，避免刷一次全量
      setTree((prev) => {
        if (!prev) return prev;
        return {
          sections: prev.sections.map((s) =>
            s.id === selectedSectionId
              ? { ...s, items: s.items.map((it) => (it.id === next.id ? next : it)) }
              : s,
          ),
        };
      });
    },
    [selectedSectionId],
  );

  const handleLocalSectionsChange = useCallback((sections: MeetingAgendaSection[]) => {
    setTree({ sections });
  }, []);

  // ─── 渲染 ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        {t.meetingAttendance.common.loading}
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-sm text-gray-700">
              {t.meetingAttendance.permissions.noAccessDescription}
            </div>
            <Link href={`/meetingattendance/meetings/${meetingId}`}>
              <Button variant="outline" size="sm" className="mt-3">
                {tag.backToMeeting}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 弹出会议已结束 warning（用户未确认前 disable 编辑）
  const editingDisabled = showEndedWarning && !warningAcknowledged;

  return (
    <div className="space-y-4 p-2">
      {/* 顶部栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href={`/meetingattendance/meetings/${meetingId}`}>
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {tag.backToMeeting}
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{tag.pageTitle}</h1>
            {meeting && (
              <p className="text-sm text-gray-500">
                {tag.meetingTitleLabel}: {meeting.title}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 已结束二次确认 */}
      <Dialog
        open={showEndedWarning && !warningAcknowledged}
        onOpenChange={(o) => {
          if (!o) {
            // 关闭 = 取消编辑回到详情页
            router.push(`/meetingattendance/meetings/${meetingId}`);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tag.editAfterEndWarning}</DialogTitle>
            <DialogDescription>{tag.editAfterEndWarning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => router.push(`/meetingattendance/meetings/${meetingId}`)}
            >
              {tag.editAfterEndCancel}
            </Button>
            <Button onClick={() => setWarningAcknowledged(true)}>{tag.editAfterEndConfirm}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 主体：左右两栏 */}
      {!editingDisabled && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* 左栏：议程 tree */}
          <Card className="lg:col-span-7">
            <CardHeader>
              <CardTitle className="text-base">{tag.leftColumnTitle}</CardTitle>
            </CardHeader>
            <CardContent>
              {tree && (
                <AgendaTree
                  meetingId={meetingId}
                  sections={tree.sections}
                  selectedItemId={selectedItem?.id || null}
                  onSelectItem={handleSelectItem}
                  onReload={reload}
                  onLocalSectionsChange={handleLocalSectionsChange}
                  onRequestAssign={setAssignFor}
                  onRequestUploadFor={setUploadFor}
                />
              )}
            </CardContent>
          </Card>

          {/* 右栏：选中 item 编辑表单 */}
          <Card className="lg:col-span-5">
            <CardHeader>
              <CardTitle className="text-base">{tag.rightColumnTitle}</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedItem && selectedSectionId ? (
                <ItemEditForm
                  sectionId={selectedSectionId}
                  columnLabels={
                    tree?.sections.find((s) => s.id === selectedSectionId)?.columnLabels ?? []
                  }
                  item={selectedItem}
                  onSaved={handleItemSaved}
                />
              ) : (
                <div className="py-8 text-center text-sm text-gray-500">{tag.rightColumnEmpty}</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* 分配任务弹窗 */}
      {assignFor && (
        <AssignTaskDialog
          open={!!assignFor}
          onOpenChange={(o) => !o && setAssignFor(null)}
          agendaItemId={assignFor.id}
          onAssigned={() => {
            reload();
          }}
        />
      )}

      {/* 上传资料弹窗（议程项级） */}
      <UploadDialog
        open={!!uploadFor}
        onOpenChange={(o) => {
          if (!o) {
            setUploadFor(null);
            reload();
          }
        }}
        uploader={itemUploader}
        title={uploadFor ? `${tag.item.uploadFile} - ${uploadFor.title}` : undefined}
      />
    </div>
  );
}
